import React, { useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrderSuccessPanel — post-save quick-action panel for salespeople.
 *
 * Shown right after `POST /api/orders` succeeds. Gives one-click access to:
 *   • Print Customer Order (opens the customer PDF in a new tab)
 *   • Download PDF (streams the customer PDF as a blob)
 *   • Send by Email (opens a mailto: with a pre-filled summary)
 *   • Send by SMS   (opens an sms: URI with the summary — mobile friendly)
 *   • Send by WhatsApp (opens https://wa.me/PHONE?text= with summary)
 *
 * The PDF is generated by GET /api/orders/{id}/customer-pdf. That endpoint
 * strips out cost, profit, supplier and internal notes — so this panel is
 * safe to share directly with the customer.
 */
export default function OrderSuccessPanel({ order, onClose, onEditFull }) {
  const { t } = useI18n();

  const phoneDigits = useMemo(
    () => (order?.customer_phone || "").replace(/[^0-9]/g, ""),
    [order?.customer_phone]
  );

  const total = Number(order?.total_amount || 0);
  const deposit = Number(order?.deposit_received || 0);
  const pending = Number(
    order?.pending_balance != null ? order.pending_balance : total - deposit
  );

  const summary = useMemo(() => {
    const lines = [
      `Rawen's · Orden #${order?.invoice_number || ""}`,
      order?.company_name ? `Cliente: ${order.company_name}` : null,
      order?.delivery_date ? `Entrega: ${order.delivery_date}` : null,
      `Total: $${total.toFixed(2)}`,
      deposit > 0 ? `Depósito: $${deposit.toFixed(2)}` : null,
      pending > 0 ? `Saldo: $${pending.toFixed(2)}` : null,
    ].filter(Boolean);
    return lines.join("\n");
  }, [order, total, deposit, pending]);

  const openCustomerPdf = () => {
    // Uses cookie-based auth already on the domain, so a direct GET works.
    window.open(`${API}/orders/${order.id}/customer-pdf`, "_blank");
  };

  const printCustomerPdf = () => {
    // Open in a new tab; the browser's default PDF viewer offers Print.
    // We deliberately don't inject an auto-print script — cookie auth on
    // a blob URL is fragile across browsers.
    openCustomerPdf();
    toast.info(t("order_success_print_hint") || "Usa Ctrl+P / ⌘+P en la pestaña abierta para imprimir.");
  };

  const downloadCustomerPdf = async () => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}/customer-pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `RAWENS-Pedido-${order.invoice_number || order.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF error");
    }
  };

  const sendByEmail = () => {
    const subject = encodeURIComponent(
      `Rawen's · Confirmación de pedido #${order?.invoice_number || ""}`
    );
    const body = encodeURIComponent(
      `${summary}\n\nGracias por su compra.\n— Equipo Rawen's`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sendBySms = () => {
    if (!phoneDigits) {
      toast.error(t("order_success_no_phone") || "El cliente no tiene teléfono registrado.");
      return;
    }
    const body = encodeURIComponent(summary);
    // iOS uses `?body=`, Android uses `?body=` too (widely supported).
    window.location.href = `sms:${phoneDigits}?body=${body}`;
  };

  const sendByWhatsapp = () => {
    if (!phoneDigits) {
      toast.error(t("order_success_no_phone") || "El cliente no tiene teléfono registrado.");
      return;
    }
    const body = encodeURIComponent(summary);
    window.open(`https://wa.me/${phoneDigits}?text=${body}`, "_blank");
  };

  const actions = [
    {
      key: "print",
      label: t("order_success_print") || "Imprimir",
      testId: "order-success-print",
      onClick: printCustomerPdf,
      icon: "🖨",
    },
    {
      key: "pdf",
      label: t("order_success_download_pdf") || "Descargar PDF",
      testId: "order-success-download-pdf",
      onClick: downloadCustomerPdf,
      icon: "⬇",
    },
    {
      key: "email",
      label: t("order_success_send_email") || "Enviar por Email",
      testId: "order-success-send-email",
      onClick: sendByEmail,
      icon: "✉",
    },
    {
      key: "sms",
      label: t("order_success_send_sms") || "Enviar por SMS",
      testId: "order-success-send-sms",
      onClick: sendBySms,
      icon: "📱",
      disabled: !phoneDigits,
    },
    {
      key: "whatsapp",
      label: t("order_success_send_whatsapp") || "Enviar por WhatsApp",
      testId: "order-success-send-whatsapp",
      onClick: sendByWhatsapp,
      icon: "🟢",
      disabled: !phoneDigits,
    },
  ];

  return (
    <div
      data-testid="order-success-overlay"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <div
        data-testid="order-success-modal"
        className="bg-white w-full max-w-2xl border border-black shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <header className="bg-black text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.22em] opacity-70">
              GP / ORDER · CREATED
            </div>
            <h2
              className="font-display text-2xl font-black tracking-tight leading-tight"
              data-testid="order-success-title"
            >
              ✓ {t("order_success_title") || "Orden creada exitosamente"}
            </h2>
          </div>
          <button
            type="button"
            data-testid="order-success-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-white/40 hover:bg-white hover:text-black transition-colors"
            aria-label="close"
          >
            ✕
          </button>
        </header>

        {/* Order summary */}
        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-[var(--border)]">
          <div>
            <div className="label-overline">
              {t("order_success_number") || "Orden"}
            </div>
            <div
              className="font-display text-lg font-black leading-tight"
              data-testid="order-success-invoice"
            >
              #{order?.invoice_number || "—"}
            </div>
          </div>
          <div>
            <div className="label-overline">
              {t("order_success_customer") || "Cliente"}
            </div>
            <div className="font-display text-sm font-bold leading-tight truncate">
              {order?.company_name || "—"}
            </div>
          </div>
          <div>
            <div className="label-overline">
              {t("order_success_delivery") || "Entrega"}
            </div>
            <div className="font-display text-sm font-bold leading-tight">
              {order?.delivery_date || "—"}
            </div>
          </div>
          <div>
            <div className="label-overline">
              {t("order_success_total") || "Total"}
            </div>
            <div
              className="font-display text-lg font-black leading-tight"
              data-testid="order-success-total"
            >
              ${total.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Actions grid */}
        <div className="px-6 py-5">
          <div className="label-overline mb-2">
            {t("order_success_share_title") || "Copia del cliente · Compartir"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                data-testid={a.testId}
                onClick={a.onClick}
                disabled={a.disabled}
                className="flex flex-col items-center justify-center gap-1.5 border border-black bg-white px-2 py-3 hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="text-xl leading-none">{a.icon}</span>
                <span className="font-display text-[0.7rem] font-bold tracking-tight text-center leading-tight">
                  {a.label}
                </span>
              </button>
            ))}
          </div>

          {!phoneDigits && (
            <div className="mt-3 text-[0.7rem] text-[var(--text-secondary)]">
              {t("order_success_phone_hint") ||
                "SMS / WhatsApp requieren un número de teléfono en el cliente."}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-[var(--border)] bg-[var(--surface-alt)] px-6 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="order-success-open-full"
            onClick={onEditFull}
            className="font-display text-xs font-bold border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors"
          >
            {t("order_success_open_full") || "Ver / editar orden completa"}
          </button>
          <button
            type="button"
            data-testid="order-success-done"
            onClick={onClose}
            className="font-display text-xs font-bold bg-black text-white px-6 py-2 hover:bg-[var(--text-secondary)] transition-colors"
          >
            {t("done") || "Listo"}
          </button>
        </footer>
      </div>
    </div>
  );
}
